import { z } from "zod";
import {
  bytesHash,
  parseProject,
  projectAssetIds,
  type Project,
} from "@proof/core";
import { checkedPreviewBlob } from "@proof/ui";

export const assetLibraryRecordSchema = z
  .object({
    id: z
      .string()
      .min(7)
      .max(500)
      .regex(/^asset_[a-zA-Z0-9_-]+$/),
    name: z.string().max(200),
    width: z.number().int().positive().max(40000000),
    height: z.number().int().positive().max(40000000),
    bytes: z.number().int().positive().max(200000000),
    tenantId: z.string().min(1).max(200).optional(),
    shared: z.boolean().optional(),
    projectIds: z.array(z.string()).optional(),
    sha256: z
      .string()
      .regex(/^[a-f0-9]{64}$/)
      .optional(),
  })
  .refine(
    (value) => value.width * value.height <= 40000000,
    "Image exceeds 40 megapixels",
  );
export type AssetLibraryRecord = z.infer<typeof assetLibraryRecordSchema>;
export interface AssetLibraryServices {
  listAssets(signal: AbortSignal): Promise<AssetLibraryRecord[]>;
  loadAsset(id: string, signal: AbortSignal): Promise<Blob>;
  uploadAsset?(
    file: File,
    signal: AbortSignal,
    scope?: "project" | "shared",
  ): Promise<AssetLibraryRecord>;
}
export interface AssetLibraryArtifact {
  blob: Blob;
  filename: string;
  record: AssetLibraryRecord;
}
export function snapshotAssetLibraryRecords(
  raw: unknown,
  tenantId: string,
): AssetLibraryRecord[] {
  const rows = z.array(assetLibraryRecordSchema).max(10000).parse(raw);
  if (
    new Set(rows.map((row) => row.id)).size !== rows.length ||
    rows.some((row) => row.tenantId !== undefined && row.tenantId !== tenantId)
  )
    throw new TypeError("Invalid or foreign asset catalog");
  return rows;
}
/** Bind prototype methods to their owner and copy data across adapter boundaries. */
export function bindAssetLibraryServices(
  input: AssetLibraryServices,
): AssetLibraryServices {
  for (const key of ["listAssets", "loadAsset"] as const)
    if (!input || typeof input[key] !== "function")
      throw new TypeError(`Provide a ${key} callback`);
  if (
    input.uploadAsset !== undefined &&
    typeof input.uploadAsset !== "function"
  )
    throw new TypeError("Provide an uploadAsset callback");
  return {
    listAssets: async (signal) => {
      signal.throwIfAborted();
      const value = await input.listAssets.call(input, signal);
      signal.throwIfAborted();
      return value;
    },
    loadAsset: async (id, signal) => {
      if (!/^asset_[a-zA-Z0-9_-]{1,494}$/.test(id))
        throw new TypeError("Invalid asset identity");
      signal.throwIfAborted();
      const raw = await input.loadAsset.call(input, id, signal);
      signal.throwIfAborted();
      if (
        Object.prototype.toString.call(raw) !== "[object Blob]" ||
        raw.size < 1 ||
        raw.size > 20000000
      )
        throw new TypeError("Invalid image bytes");
      const bytes = await Blob.prototype.arrayBuffer.call(raw);
      signal.throwIfAborted();
      return checkedPreviewBlob(new Blob([bytes], { type: raw.type }));
    },
    ...(input.uploadAsset && {
      uploadAsset: async (
        file: File,
        signal: AbortSignal,
        scope?: "project" | "shared",
      ) => {
        if (
          file.size < 1 ||
          file.size > 10000000 ||
          !/\.(png|jpe?g|webp|tiff?)$/i.test(file.name)
        )
          throw new TypeError(
            "Choose a PNG, JPEG, WebP or TIFF image up to 10 MB",
          );
        signal.throwIfAborted();
        const record = await input.uploadAsset!.call(
          input,
          file,
          signal,
          scope,
        );
        signal.throwIfAborted();
        return assetLibraryRecordSchema.parse(record);
      },
    }),
  };
}
export function attachLibraryAsset(
  project: Project,
  selected: AssetLibraryRecord,
  catalog: readonly AssetLibraryRecord[],
): Project {
  const records = snapshotAssetLibraryRecords(catalog, project.tenantId),
    record = records.find((row) => row.id === selected.id);
  if (
    !record ||
    JSON.stringify(record) !==
      JSON.stringify(
        snapshotAssetLibraryRecords([selected], project.tenantId)[0],
      )
  )
    throw new TypeError("Choose an asset from this catalog");
  const next = parseProject(structuredClone(project));
  next.assetLibrary = [
    ...(next.assetLibrary || []).filter((entry) => entry.assetId !== record.id),
    { assetId: record.id },
  ];
  return next;
}
export function detachLibraryAsset(
  project: Project,
  id: string,
  catalog: readonly AssetLibraryRecord[],
): Project {
  const records = snapshotAssetLibraryRecords(catalog, project.tenantId);
  if (!records.some((row) => row.id === id))
    throw new TypeError("Choose an asset from this catalog");
  if (projectAssetIds(project, records, { includeLibrary: false }).has(id))
    throw new TypeError("This image is used by the document");
  const next = parseProject(structuredClone(project));
  if (next.assetLibrary)
    next.assetLibrary = next.assetLibrary.filter(
      (entry) => entry.assetId !== id,
    );
  return next;
}
export async function readLibraryAsset(
  record: AssetLibraryRecord,
  services: AssetLibraryServices,
  signal: AbortSignal,
): Promise<AssetLibraryArtifact> {
  record = assetLibraryRecordSchema.parse(record);
  const blob = await bindAssetLibraryServices(services).loadAsset(
    record.id,
    signal,
  );
  signal.throwIfAborted();
  if (blob.size !== record.bytes)
    throw new TypeError("Image size differs from its catalog record");
  const hash = record.sha256 || /^asset_([a-f0-9]{64})$/.exec(record.id)?.[1];
  if (hash && bytesHash(new Uint8Array(await blob.arrayBuffer())) !== hash)
    throw new TypeError("Image bytes differ from their managed identity");
  signal.throwIfAborted();
  const extension =
    blob.type === "image/jpeg"
      ? "jpg"
      : blob.type === "image/webp"
        ? "webp"
        : "png";
  const stem =
    (record.name.split(/[\\/]/).pop() || "image")
      .replace(/\.[^.]*$/, "")
      .replace(/[^\p{L}\p{N} _.\-]/gu, "_")
      .slice(0, 100) || "image";
  return {
    blob,
    filename: `${stem}.${extension}`,
    record: structuredClone(record),
  };
}
export function downloadLibraryAsset(
  artifact: AssetLibraryArtifact,
  doc: Document = document,
) {
  const urls = doc.defaultView?.URL || URL,
    url = urls.createObjectURL(artifact.blob),
    anchor = doc.createElement("a");
  anchor.href = url;
  anchor.download = artifact.filename;
  anchor.click();
  setTimeout(() => urls.revokeObjectURL(url), 1000);
}
